import React, { useState, useRef, useEffect } from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik, Field, ErrorMessage, useFormikContext } from "formik";
import toast from "react-hot-toast";
import {
  createOrganizationAPI,
  organizationListAPI,
} from "../../../redux/features/authSlice";
import DottedLoading from "../../../components/DottedLoading/DottedLoading";
import { GoogleMap, useLoadScript, Autocomplete } from "@react-google-maps/api";
import Select from "react-select"; // Import react-select
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-input-2";
const libraries = ["places"];

const validationSchemas = [
  Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    contact_number: Yup.string()
      .required("Phone number is required")
      .test("is-valid-phone", "Invalid phone number", (value) => {
        if (!value) return false;
        const cleaned = value.replace(/\D/g, "");
        return cleaned.length >= 10 && cleaned.length <= 15;
      }),
    designation: Yup.string().required("Designation is required"),
    message: Yup.string()
      .min(10, "Message must be at least 10 characters")
      .required("Message is required"),
  }),
  Yup.object({
    organization_name: Yup.string().required("Organization is required"),
    industry_type: Yup.string().required("Industry Type is required"),
    number_of_students: Yup.string()
      .min(1, "Number of students must be at least 1")
      .max(1000000, "Number of students must be at most 1,000,000")
      .required("Number of students is required"),
    website_url: Yup.string()
      .required("Website URL is required")
      .matches(
        /^(https?:\/\/)?([\w\-]+\.)+[\w\-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/,
        "Must be a valid URL"
      )
      .max(2048, "URL must be at most 2048 characters"),
  }),
  Yup.object({
    location: Yup.string().required("Location is required"),
    latitude: Yup.number().nullable(),
    longitude: Yup.number().nullable(),
  }),
];

// Initial form values
const initialValues = {
  name: "",
  email: "",
  contact_number: "",
  designation: "",
  message: "",
  organization_name: "",
  industry_type: "",
  number_of_students: "",
  website_url: "",
  location: "",
  latitude: null,
  longitude: null,
};

// Submit button component
const SubmitButton = ({ step, isSubmitting, buttonLoader }) => {
  const { submitForm } = useFormikContext();
  return (
    <button
      className="btn btn-primary w-100 mb-16 mb-sm-0"
      type="button"
      disabled={buttonLoader || isSubmitting}
      onClick={submitForm}
    >
      {step === 3 ? !buttonLoader ? "Submit" : <DottedLoading /> : "Next"}
    </button>
  );
};

// Autocomplete component using @react-google-maps/api
const PlaceAutocomplete = ({ setFieldValue }) => {
  const autocompleteRef = useRef(null);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.address_components) {
      let country = "";
      let city = "";
      let state_province = "";
      let postal_code = "";
      let location = place.formatted_address || "";
      let latitude = place.geometry?.location?.lat() || null;
      let longitude = place.geometry?.location?.lng() || null;

      place.address_components.forEach((component) => {
        if (component.types.includes("country")) {
          country = component.long_name;
        }
        if (
          component.types.includes("locality") ||
          component.types.includes("sublocality")
        ) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state_province = component.long_name;
        }
        if (component.types.includes("postal_code")) {
          postal_code = component.long_name;
        }
      });

      setFieldValue("location", location);
      setFieldValue("country", country);
      setFieldValue("city", city);
      setFieldValue("state_province", state_province);
      setFieldValue("postal_code", postal_code);
      setFieldValue("latitude", latitude);
      setFieldValue("longitude", longitude);
    }
  };

  return (
    <div className="mb-12 mb-md-16">
      <label
        htmlFor="autocomplete"
        className="form-label black-100 fw-semibold mb-8 mb-lg-12"
      >
        Location
      </label>
      <Autocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
        }}
        onPlaceChanged={onPlaceChanged}
        fields={["address_components", "formatted_address", "geometry"]}
      >
        <Field
          type="text"
          id="autocomplete"
          name="location"
          className="form-control"
          placeholder="Enter a location"
          onChange={(e) => setFieldValue("location", e.target.value)}
        />
      </Autocomplete>
      <ErrorMessage
        name="location"
        component="div"
        className="text-danger mt-2"
      />
    </div>
  );
};

const Organization = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState("US");
  const [industryList, setIndustryList] = useState([]); // Fixed typo: instustryList -> industryList

  // Fetch user's country based on IP and industry list
  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await fetch(
          "https://api.ipgeolocation.io/ipgeo?apiKey=a648d260fc9d43dfa2a8ec35e0572235"
        );
        const data = await response.json();
        if (data?.country_code2) {
          setDefaultCountry(data.country_code2.toLowerCase());
        }
      } catch (error) {
        console.error("Failed to fetch country from IP:", error);
      }
    };

    const organizationFetchCall = async () => {
      try {
        const res = await dispatch(organizationListAPI()).unwrap();
        if (res.success) {
          const industryOptionsArr = res.data.map((industry) => ({
            label: industry.name,
            value: industry.name, // Use name as value for consistency with form
          }));
          setIndustryList(industryOptionsArr);
        }
      } catch (error) {
        console.error("Failed to fetch industry list:", error);
        toast.error("Failed to load industry options");
      }
    };

    fetchCountry();
    organizationFetchCall();
  }, [dispatch]);

  // Load Google Maps script with Places library
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const handleSubmit = async (values, { setSubmitting, setTouched }) => {
    try {
      if (step < 3) {
        setStep(step + 1);
        setTouched({});
      } else {
        setButtonLoader(true);
        const res = await dispatch(createOrganizationAPI(values)).unwrap();
        if (res.success) {
          toast.success(res.message);
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setButtonLoader(false);
      setSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (step > 1) setStep(step - 1);
  };

  if (loadError) {
    return <div>Error loading Google Maps API</div>;
  }

  if (!isLoaded) {
    return <DottedLoading />;
  }

  return (
    <div className="d-flex flex-column flex-grow-1 justify-content-center px-15">
      {step === 1 && (
        <div className="text-center mb-20">
          <h1 className="ah-heading-02 fw-semibold mb-20">
            Tell us about yourself
          </h1>
          <p className="ah-text-base">
            Kindly fill out the form. Our team will reach out to you soon.
          </p>
        </div>
      )}
      {step === 2 && (
        <div className="text-center mb-20">
          <h1 className="ah-heading-02 fw-semibold mb-20">Basic Information</h1>
        </div>
      )}
      {step === 3 && (
        <div className="text-center mb-20">
          <h1 className="ah-heading-02 fw-semibold mb-20">
            Address & Location
          </h1>
        </div>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemas[step - 1]}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange
      >
        {({ isSubmitting, setFieldValue, values, errors, touched }) => {
          // const phoneError =
          //   values.contact_number && !isValidPhoneNumber(values.contact_number)
          //     ? "Phone number is invalid"
          //     : undefined;
          return (
            <div className="bg-white auth-form-holder text-left mx-auto px-20 px-sm-30 px-md-40 pt-40 pt-md-50 pb-30">
              {step === 1 && (
                <>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="name"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Full Name
                    </label>
                    <Field
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      placeholder="Enter Your Full Name"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="email"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Email
                    </label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="Enter Your Email"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="contact_number"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Phone Number
                    </label>
                    <div>
                      <PhoneInput
                        country={defaultCountry}
                        value={values.contact_number}
                        onChange={(value) =>
                          setFieldValue(
                            "contact_number",
                            "+" + value.replace(/^\+/, "")
                          )
                        }
                        enableSearch={true}
                        countryCodeEditable={false}
                        inputProps={{
                          name: "contact_number",
                          required: true,
                          className: "form-control",
                        }}
                      />
                    </div>
                    <ErrorMessage
                      name="contact_number"
                      component="div"
                      className="text-danger mt-2"
                    />
                    {/* {phoneError && (
                      <div className="text-danger mt-2">{phoneError}</div>
                    )} */}
                  </div>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="designation"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Your Designation
                    </label>
                    <Field
                      type="text"
                      id="designation"
                      name="designation"
                      className="form-control"
                      placeholder="Enter your designation"
                    />
                    <ErrorMessage
                      name="designation"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="message"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      About the organization
                    </label>
                    <Field
                      as="textarea"
                      id="message"
                      name="message"
                      rows="4"
                      className="form-control h-auto"
                      placeholder="Write message here"
                    />
                    <ErrorMessage
                      name="message"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                </>
              )}
              {step === 2 && (
                <>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="organization_name"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Organization Name
                    </label>
                    <Field
                      type="text"
                      id="organization_name"
                      name="organization_name"
                      className="form-control"
                      placeholder="Enter organization name"
                    />
                    <ErrorMessage
                      name="organization_name"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="industry_type"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Industry Type
                    </label>
                    <Select
                      id="industry_type"
                      name="industry_type"
                      options={industryList}
                      value={industryList.find(
                        (option) => option.value === values.industry_type
                      )}
                      onChange={(option) =>
                        setFieldValue(
                          "industry_type",
                          option ? option.value : ""
                        )
                      }
                      placeholder="Select or type to search industry"
                      isClearable
                      isSearchable
                      classNamePrefix="react-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor: state.isFocused
                            ? "#ced4da"
                            : errors.industry_type && touched.industry_type
                            ? "#dc3545"
                            : "#ced4da",
                          boxShadow: state.isFocused
                            ? "0 0 0 0.2rem rgba(0,123,255,.25)"
                            : "none",
                          "&:hover": {
                            borderColor: state.isFocused
                              ? "#ced4da"
                              : "#ced4da",
                          },
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                    <ErrorMessage
                      name="industry_type"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="number_of_students"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Number of Students
                    </label>
                    <Field
                      type="number"
                      id="number_of_students"
                      name="number_of_students"
                      className="form-control"
                      placeholder="Enter number of students"
                    />
                    <ErrorMessage
                      name="number_of_students"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                  <div className="mb-12 mb-md-16">
                    <label
                      htmlFor="website_url"
                      className="form-label black-100 fw-semibold mb-8 mb-lg-12"
                    >
                      Website URL
                    </label>
                    <Field
                      type="text"
                      id="website_url"
                      name="website_url"
                      className="form-control"
                      placeholder="Enter website URL"
                    />
                    <ErrorMessage
                      name="website_url"
                      component="div"
                      className="text-danger mt-2"
                    />
                  </div>
                </>
              )}
              {step === 3 && (
                <>
                  <PlaceAutocomplete setFieldValue={setFieldValue} />
                </>
              )}
              <div className="pt-12 mb-18 mb-md-24 d-sm-flex gap-3">
                {step > 1 && (
                  <button
                    type="button"
                    className="btn btn-secondary w-100 mb-16 mb-sm-0"
                    onClick={handlePrevious}
                  >
                    Back
                  </button>
                )}
                <SubmitButton
                  step={step}
                  isSubmitting={isSubmitting}
                  buttonLoader={buttonLoader}
                />
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default Organization;
